import React, { useCallback } from 'react';
import { OnChangeValue } from 'react-select';
import { AsyncSelect, ISelectOption } from '../../base';
import { searchSupportTags } from '../../../services/worker-configuration';
import * as S from './configuration-rule.styles';

interface IConfigurationTagListEditorProps {
    /** When false, shows selected names only and never calls GET /support/tag. */
    allowSearch?: boolean;
    disabled?: boolean;
    onChange: (tagNames: string[]) => void;
    tagNames: string[];
}

function toOption(name: string): ISelectOption {
    return {
        label: name,
        value: name,
    };
}

export const ConfigurationTagListEditor: React.FC<IConfigurationTagListEditorProps> = ({
    allowSearch = true,
    disabled,
    onChange,
    tagNames,
}) => {
    const selectedValue = tagNames.map(toOption);

    const loadOptions = useCallback(
        async (inputValue: string): Promise<ISelectOption[]> => {
            if (!allowSearch) {
                return [];
            }

            const tags = await searchSupportTags(inputValue);
            return tags.map(tag => toOption(tag.name));
        },
        [allowSearch],
    );

    const handleChange = (selected: OnChangeValue<ISelectOption, true>): void => {
        if (!allowSearch || disabled) {
            return;
        }

        const names = selected
            .map(entry => String(entry.value ?? '').trim())
            .filter(Boolean);

        // Empty list configs must persist as [] rather than null.
        onChange(names);
    };

    if (!allowSearch) {
        return (
            <S.PayerEditor>
                <S.FieldSelect
                    isMulti
                    isDisabled
                    isClearable={false}
                    options={selectedValue}
                    value={selectedValue}
                    placeholder="No claim tags configured"
                />
            </S.PayerEditor>
        );
    }

    return (
        <S.PayerEditor>
            <AsyncSelect
                isMulti={true}
                isDisabled={disabled}
                isClearable={true}
                closeMenuOnSelect={false}
                defaultOptions={true}
                cacheOptions={true}
                placeholder="Search claim tags…"
                value={selectedValue}
                loadOptions={loadOptions}
                onChange={selected => handleChange(selected as OnChangeValue<ISelectOption, true>)}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                menuPosition="fixed"
            />
        </S.PayerEditor>
    );
};
